import { ctxRoundRect, formatPriceValue, formatTime } from "./chart-utils";

export interface CrosshairPosition {
  x: number;
  y: number;
  price: number;
  time: number;
  timestamp: number;
}

interface DrawCrosshairParams {
  ctx: CanvasRenderingContext2D;
  position: CrosshairPosition;
  canvasWidth: number;
  canvasHeight: number;
  chartOffsetX: number;
  rightPadding: number;
  bottomPadding: number;
  isMobile: boolean;
  windowSize: number;
  timeInterval?: string;
  timezone?: string;
  pricePrecision?: number;
}

export const drawCrosshair = (params: DrawCrosshairParams) => {
  const {
    ctx,
    position,
    canvasWidth,
    canvasHeight,
    chartOffsetX,
    rightPadding,
    bottomPadding,
    isMobile,
    windowSize,
    timeInterval,
    timezone,
    pricePrecision,
  } = params;

  const chartHeight = canvasHeight - bottomPadding;
  const chartEndX = canvasWidth - rightPadding;

  if (
    !Number.isFinite(position.x) ||
    !Number.isFinite(position.y) ||
    !Number.isFinite(position.price) ||
    !Number.isFinite(position.time) ||
    !Number.isFinite(position.timestamp)
  ) {
    return;
  }

  if (position.x < chartOffsetX || position.x > chartEndX || position.y < 0 || position.y > chartHeight) {
    return;
  }

  ctx.save();

  //Vertical line
  ctx.strokeStyle = "#9ca3af";
  ctx.lineWidth = 1;
  ctx.setLineDash([5, 5]);
  ctx.globalAlpha = 0.7;

  ctx.beginPath();
  ctx.moveTo(position.x, 0);
  ctx.lineTo(position.x, chartHeight);
  ctx.stroke();

  //Horizontal line
  ctx.beginPath();
  ctx.moveTo(chartOffsetX, position.y);
  ctx.lineTo(canvasWidth - rightPadding, position.y);
  ctx.stroke();

  ctx.setLineDash([]);
  ctx.globalAlpha = 1;

  //Price label on Y axis
  const priceText = formatPriceValue(position.price, pricePrecision);
  ctx.font = `${isMobile ? 11 : 12}px monospace`;
  const priceTextWidth = ctx.measureText(priceText).width;

  const priceLabelWidth = priceTextWidth + 16;
  const priceLabelHeight = isMobile ? 20 : 24;
  const priceLabelX = canvasWidth - rightPadding - priceLabelWidth;
  const priceLabelY = position.y - priceLabelHeight / 2;

  //Price label background
  ctx.fillStyle = "#333333";
  ctx.beginPath();
  ctxRoundRect(ctx, priceLabelX, priceLabelY, priceLabelWidth, priceLabelHeight, 3);
  ctx.fill();

  //Price label text
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  ctx.fillText(priceText, priceLabelX + 8, position.y);

  //Time label on X axis
  const date = new Date(position.timestamp);
  const showSeconds = windowSize <= 180;
  const timeText = formatTime(date, showSeconds, Date.now(), timeInterval === "1D", timezone);

  ctx.font = `${isMobile ? 10 : 11}px monospace`;
  const timeTextWidth = ctx.measureText(timeText).width;

  const timeLabelWidth = timeTextWidth + 12;
  const timeLabelHeight = isMobile ? 18 : 20;
  const timeLabelX = position.x - timeLabelWidth / 2;
  const timeLabelY = chartHeight + 2;

  //Time label background
  ctx.fillStyle = "#333333";
  ctx.beginPath();
  ctxRoundRect(ctx, timeLabelX, timeLabelY, timeLabelWidth, timeLabelHeight, 3);
  ctx.fill();

  //Time label text
  ctx.fillStyle = "#ffffff";
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillText(timeText, position.x, timeLabelY + 6);

  //Center point of crosshair
  ctx.fillStyle = "#F5A524";
  ctx.beginPath();
  ctx.arc(position.x, position.y, 4, 0, 2 * Math.PI);
  ctx.fill();

  //Center point of crosshair outline
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 1.5;
  ctx.stroke();

  ctx.restore();
};

export const convertMouseToChartCoordinates = (
  mouseX: number,
  mouseY: number,
  canvasRect: DOMRect,
  chartParams: {
    chartOffsetX: number;
    drawWidth: number;
    chartHeight: number;
    currentViewportTime: number;
    windowSize: number;
    minPrice: number;
    maxPrice: number;
    baseTimeRef: number;
    canvasWidth?: number;
    rightPadding?: number;
    emptySpaceFraction?: number;
  },
): CrosshairPosition | null => {
  const {
    chartOffsetX,
    drawWidth,
    chartHeight,
    currentViewportTime,
    windowSize,
    minPrice,
    maxPrice,
    baseTimeRef,
    canvasWidth = canvasRect.width,
    rightPadding = 0,
    emptySpaceFraction = 0,
  } = chartParams;

  const x = mouseX - canvasRect.left;
  const y = mouseY - canvasRect.top;

  const chartEndX = canvasWidth - rightPadding;

  if (
    drawWidth <= 0 ||
    chartHeight <= 0 ||
    !Number.isFinite(drawWidth) ||
    !Number.isFinite(chartHeight) ||
    !Number.isFinite(currentViewportTime) ||
    !Number.isFinite(windowSize) ||
    !Number.isFinite(baseTimeRef) ||
    !Number.isFinite(minPrice) ||
    !Number.isFinite(maxPrice)
  ) {
    return null;
  }

  // Keep hover state inside the same drawable chart bounds used by drawCrosshair.
  if (x < chartOffsetX || x > chartEndX || y < 0 || y > chartHeight) {
    return null;
  }

  // Calculate time using the same coordinate system as drawChart:
  // viewportStartTime = currentViewportTime - windowSize * (1 - emptySpaceFraction)
  // time = viewportStartTime + (xPosition / drawWidth) * windowSize
  // This unified formula covers both chart area and future (emptySpace) zone.
  const xPosition = x - chartOffsetX;
  const viewportStartTime = currentViewportTime - windowSize * (1 - emptySpaceFraction);
  const time = viewportStartTime + (xPosition / drawWidth) * windowSize;

  const timestamp = baseTimeRef + time * 1000;

  const priceRange = maxPrice - minPrice;
  const normalizedY = 1 - y / chartHeight;
  const price = minPrice + normalizedY * priceRange;

  if (
    !Number.isFinite(time) ||
    !Number.isFinite(timestamp) ||
    !Number.isFinite(price) ||
    !Number.isFinite(priceRange)
  ) {
    return null;
  }

  return {
    x,
    y,
    price,
    time,
    timestamp,
  };
};
